/**
 * AdminCourses.jsx
 * Admin dashboard listing all courses stored in MongoDB.
 * Mirrors the AdminBlogs.jsx pattern: table with slug, title, status, order, and actions.
 *
 * Routes: /admin/courses
 * Actions: Edit (→ /admin/courses/edit/:id), Delete, Create New (→ /admin/courses/new)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../homepage/navbar/Navbar';
import { useThemeContext } from '../../context/theme-context';
import './admin.css';

const AdminCourses = () => {
  const { themeState } = useThemeContext();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null); // Tracks which row is being deleted

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

  // ── Fetch all courses (including unpublished) ──────────────────────────────
  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/courses/admin/all`, {
        credentials: 'include',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok && json.success) {
        setCourses(json.data || []);
      } else {
        setError(json.message || 'Failed to load courses');
      }
    } catch (err) {
      setError('Could not connect to the server.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Toggle publish status ──────────────────────────────────────────────────
  const handleTogglePublish = async (course) => {
    try {
      const token = localStorage.getItem('token');
      // We must provide slug and title since the backend PUT route requires them.
      const updatedData = { ...course, published: !course.published };
      const res = await fetch(`${API_URL}/api/courses/${course._id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(updatedData),
      });

      if (res.status === 401 || res.status === 403) {
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok && json.success) {
        setCourses((prev) =>
          prev.map((c) => (c._id === course._id ? { ...c, published: !c.published } : c))
        );
      } else {
        setError(json.message || 'Failed to update course status');
      }
    } catch (err) {
      setError('Failed to update status.');
      console.error(err);
    }
  };

  // ── Delete course ──────────────────────────────────────────────────────────
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This action cannot be undone.`)) return;

    try {
      setDeletingId(id);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/courses/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });

      if (res.status === 401 || res.status === 403) {
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok && json.success) {
        setCourses((prev) => prev.filter((c) => c._id !== id));
      } else {
        setError(json.message || 'Failed to delete course');
      }
    } catch (err) {
      setError('Delete request failed.');
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <main className={`${themeState.primary} ${themeState.background} admin__page`}>
      <Navbar />

      <section className="admin__container">
        <br />
        {/* Header row */}
        <div
          className="admin__header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn sm" onClick={() => navigate('/admin')}>← Back</button>
            <h2>Courses Management</h2>
          </div>
          <button
            className="btn primary"
            onClick={() => navigate('/admin/courses/new')}
          >
            + New Course
          </button>
        </div>

        {/* Status messages */}
        {error && <div className="admin__error">{error}</div>}
        {loading && <div className="admin__loading">Loading courses...</div>}

        {/* Courses table */}
        {!loading && courses.length === 0 && !error && (
          <div className="admin__message" style={{ marginTop: '2rem', textAlign: 'center', opacity: 0.7 }}>
            No courses in the database yet.{' '}
            <button className="btn sm primary" onClick={() => navigate('/admin/courses/new')}>
              Create the first one
            </button>
          </div>
        )}

        {!loading && courses.length > 0 && (
          <div className="admin__table-wrap" style={{ marginTop: '1.5rem' }}>
            <table className="admin__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Slug</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course, idx) => (
                  <tr key={course._id}>
                    <td>{idx + 1}</td>
                    <td>
                      <code style={{ fontSize: '0.85rem', opacity: 0.8 }}>{course.slug}</code>
                    </td>
                    <td className="admin__message" style={{ fontWeight: 600 }}>
                      {course.title}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: '0.2rem 0.6rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          background: course.published
                            ? 'rgba(76,175,80,0.15)'
                            : 'rgba(158,158,158,0.2)',
                          color: course.published ? '#2e7d32' : '#757575',
                        }}
                      >
                        {course.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>{course.order ?? 0}</td>
                    <td style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {/* View live page */}
                      <button
                        className="btn sm"
                        onClick={() =>
                          window.open(`/virtual-internship/${course.slug}`, '_blank')
                        }
                        title="View live page"
                      >
                        View
                      </button>
                      {/* Show / Hide toggle */}
                      <button
                        className="btn sm"
                        onClick={() => handleTogglePublish(course)}
                        title={course.published ? 'Hide course' : 'Show course'}
                      >
                        {course.published ? '👁 Hide' : '👁 Show'}
                      </button>
                      {/* Edit */}
                      <button
                        className="btn sm primary"
                        onClick={() => navigate(`/admin/courses/edit/${course._id}`)}
                        title="Edit course"
                      >
                        Edit
                      </button>
                      {/* Delete */}
                      <button
                        className="btn sm"
                        style={{ background: 'rgba(211,47,47,0.1)', color: '#c62828' }}
                        onClick={() => handleDelete(course._id, course.title)}
                        disabled={deletingId === course._id}
                        title="Delete course"
                      >
                        {deletingId === course._id ? '...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default AdminCourses;
